'use client';
import React from 'react';
import Image from 'next/image';
import AnnouncementBar from '@/components/AnnouncementBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PsychologicalServicesPage() {
  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <style>{`
        .cta-btn-dark {
          background-color: #1A2332;
          transition: background-color 0.25s ease;
        }
        .cta-btn-dark:hover {
          background-color: #2C2C2C;
        }
        .ps-section-label {
          font-family: 'adobe-garamond-pro', Georgia, serif;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #8A9E8C;
          margin-bottom: 16px;
          display: block;
        }
        .ps-section-heading {
          font-family: 'orpheus-pro', Georgia, serif;
          font-weight: 400;
          color: #1A2332;
          line-height: 1.15;
        }
        .ps-body-text {
          font-family: 'adobe-garamond-pro', Georgia, serif;
          font-size: clamp(16px, 1.3vw, 19px);
          color: #4A4A4A;
          line-height: 1.85;
        }
        .ps-divider {
          width: 40px;
          height: 1px;
          background-color: #8A9E8C;
          margin-bottom: 32px;
        }
      `}</style>
      <AnnouncementBar />
      <Header />
      <main>
        {/* ── Page Hero — staircase image (same as Treatment Stages) ── */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: '#1A2332',
            paddingTop: 'clamp(72px, 9vw, 128px)',
            paddingBottom: 'clamp(72px, 9vw, 128px)'
          }}>
          
          <Image
            src="/assets/images/image_2026-06-21_183958361-1782063601290.png"
            alt="Elegant staircase — a metaphor for the journey of psychological growth"
            fill
            sizes="100vw"
            quality={80}
            priority
            className="object-cover"
            style={{ objectPosition: 'center' }} />
          
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(26,35,50,0.62)' }} />
          
          <div
            className="relative z-10"
            style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            
            <p className="ps-section-label" style={{ color: '#8A9E8C' }}>
              Clinical Practice
            </p>
            <h1
              className="ps-section-heading"
              style={{
                fontSize: 'clamp(36px, 5vw, 72px)',
                color: '#F5F0EA',
                maxWidth: '640px',
                marginBottom: '24px'
              }}>
              
              Psychological<br />
              <em style={{ fontStyle: 'normal' }}>Services</em>
            </h1>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(16px, 1.4vw, 20px)',
                color: 'rgba(239,227,225,0.65)',
                lineHeight: '1.7',
                maxWidth: '520px'
              }}>
              
              Highly individualised, formulation-led care across therapy, assessment, supervision and consultancy.
            </p>
          </div>
        </section>

        {/* ── Section 1: Individual Therapy ── */}
        <section
          className="w-full"
          style={{
            backgroundColor: '#FFFFFF',
            paddingTop: 'clamp(80px, 10vw, 128px)',
            paddingBottom: 'clamp(80px, 10vw, 128px)'
          }}>
          
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row items-start gap-16 lg:gap-24">
              {/* Text column */}
              <div style={{ flex: '1 1 55%' }}>
                <span className="ps-section-label">Section One</span>
                <div className="ps-divider" />
                <h2
                  className="ps-section-heading"
                  style={{ fontSize: 'clamp(28px, 3.5vw, 52px)', marginBottom: '36px' }}>
                  
                  Individual<br />Therapy
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                  {[
                  'Many of the symptoms we experience and the situations that most trigger us are often communicating earlier attachment wounds and unresolved experiences. So, no two people will experience psychological difficulties in the same way. For this reason, I do not believe in a one-size-fits-all approach to therapy.',
                  'Every course of therapy begins with a comprehensive psychological formulation, allowing treatment to be guided by a deep understanding of your experiences, strengths and goals rather than a standardised protocol. This formulation provides the foundation for a therapeutic approach that is carefully tailored to your individual needs and can evolve as your understanding, circumstances and priorities change.',
                  'My work is integrative, drawing on a range of evidence-based psychological models to create a treatment approach that is truly personalised. With advanced training in Cognitive Behavioural Therapy (CBT), Dialectical Behaviour Therapy (DBT), Compassion Focused Therapy (CFT), Cognitive Analytic Therapy (CAT), together with psychodynamic approaches, I carefully adapt therapy to ensure it reflects the person rather than asking the person to fit a particular therapeutic model.',
                  'This flexibility allows therapy to evolve alongside you. At times, a practical, skills-based approach may be most helpful; at others, deeper exploration of longstanding patterns, relationships and emotional experiences may be more appropriate. By integrating different therapeutic perspectives, I provide care that is both scientifically grounded and deeply personalised.']?.
                  map((para, idx) =>
                  <p key={idx} className="ps-body-text">{para}</p>
                  )}
                </div>
              </div>

              {/* Image + pull quote column */}
              <div style={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {/* Butterfly image with black background — contain so full wings are visible */}
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '4/3',
                    overflow: 'hidden',
                    backgroundColor: '#000000'
                  }}>
                  
                  <Image
                    src="/assets/images/image-1783886136175.png"
                    alt="Butterfly — a symbol of transformation and psychological growth"
                    fill
                    sizes="(max-width: 1024px) 100vw, 40vw"
                    className="object-contain"
                    style={{ objectPosition: 'center' }} />
                  
                </div>
                {/* Text beneath image */}
                <p
                  style={{
                    fontFamily: 'orpheus-pro, Georgia, serif',
                    fontSize: 'clamp(16px, 1.5vw, 22px)',
                    fontWeight: '400',
                    color: '#000000',
                    lineHeight: '1.45',
                    fontStyle: 'italic',
                    margin: 0
                  }}>
                  Transforming your relationship with self and others
                </p>
              </div>
            </div>

            {/* Closing paragraph — full width */}
            <div style={{ marginTop: '48px', maxWidth: '760px' }}>
              <p className="ps-body-text">
                My aim is to create a collaborative, thoughtful and confidential space in which lasting psychological change can occur.
              </p>
            </div>
          </div>
        </section>

        {/* ── Section 2: Specialist Psychological Assessments ── */}
        <section
          className="w-full"
          style={{
            backgroundColor: '#F7F3F2',
            paddingTop: 'clamp(80px, 10vw, 128px)',
            paddingBottom: 'clamp(80px, 10vw, 128px)'
          }}>
          
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            {/* Section header — centred intro */}
            <div style={{ maxWidth: '680px', marginBottom: 'clamp(48px, 6vw, 80px)' }}>
              <span className="ps-section-label">Section Two</span>
              <div className="ps-divider" />
              <h2
                className="ps-section-heading"
                style={{ fontSize: 'clamp(28px, 3.5vw, 52px)', marginBottom: '28px' }}>
                
                Specialist Psychological<br />Assessments
              </h2>
              <p className="ps-body-text">
                I provide comprehensive specialist psychological assessments for adults across a range of clinical, cognitive and medico-legal presentations. My assessment work is grounded in over 20 years of clinical experience and informed by a formulation-led approach that seeks to develop a clear and meaningful understanding of each person's cognitive, emotional and psychological functioning.
              </p>
            </div>

            {/* Two-column: image left, text right */}
            <div className="flex flex-col lg:flex-row-reverse items-start gap-16 lg:gap-24">
              {/* Text */}
              <div style={{ flex: '1 1 55%', display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {[
                'I am trained and experienced in undertaking assessments for ADHD, cognitive ability (IQ), personality functioning, trauma-related difficulties, dementia and memory concerns, and mental capacity. In addition, I have specialist training in a range of neuropsychological and neurological-style assessments, supporting the evaluation of cognitive functioning in the context of broader neurological or complex clinical presentations.',
                'Each assessment is carefully tailored to the individual\'s unique circumstances, ensuring that findings are clinically robust, clearly articulated and relevant to real-world functioning.',
                'Where appropriate, I also undertake medico-legal assessments within the family court system, providing independent expert opinion to assist legal decision-making. This work is approached with particular care, objectivity and attention to detail, recognising the importance of offering clear, balanced and evidence-based conclusions within complex and often highly sensitive contexts.',
                'My aim is to provide not only diagnostic clarity where appropriate, but also a deeper understanding of strengths, difficulties and underlying psychological processes. Reports are written to be clinically rigorous, accessible, and useful to both clients and referring professionals, supporting informed decision-making and appropriate care planning.',
                'Assessments can be undertaken as part of a broader therapeutic or consultative package, or as a standalone service.']?.
                map((para, idx) =>
                <p key={idx} className="ps-body-text">{para}</p>
                )}
              </div>

              {/* Image */}
              <div style={{ flex: '1 1 40%' }}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '2/3',
                    overflow: 'hidden',
                    backgroundColor: '#DFD0CC'
                  }}>
                  
                  <Image
                    src="/assets/images/image-1783806830723.png"
                    alt="Desk and window in a calm professional clinical setting"
                    fill
                    sizes="(max-width: 1024px) 100vw, 40vw"
                    className="object-cover"
                    style={{ objectPosition: 'center' }} />
                  
                </div>
                {/* Accent note */}
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '13px',
                    letterSpacing: '0.08em',
                    color: '#8A9E8C',
                    marginTop: '20px',
                    textTransform: 'uppercase'
                  }}>
                  
                  Clinically rigorous · Clearly articulated · Individually tailored
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 3: Clinical Supervision ── */}
        <section
          className="w-full"
          style={{
            backgroundColor: '#FFFFFF',
            paddingTop: 'clamp(80px, 10vw, 128px)',
            paddingBottom: 'clamp(80px, 10vw, 128px)'
          }}>
          
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row items-start gap-16 lg:gap-24">
              {/* Image */}
              <div style={{ flex: '1 1 50%' }}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '4/3',
                    overflow: 'hidden',
                    backgroundColor: '#EFE3E1'
                  }}>
                  
                  <Image
                    src="/assets/images/image-1783806834437.png"
                    alt="Two chairs in a calm supervision or consultation room"
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover"
                    style={{ objectPosition: 'center' }} />
                  
                </div>
              </div>

              {/* Text */}
              <div style={{ flex: '1 1 45%' }}>
                <span className="ps-section-label">Section Three</span>
                <div className="ps-divider" />
                <h2
                  className="ps-section-heading"
                  style={{ fontSize: 'clamp(28px, 3.5vw, 52px)', marginBottom: '36px' }}>
                  
                  Clinical<br />Supervision
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                  {[
                  'I provide high-quality clinical supervision to psychologists and a wide range of health professionals working across NHS, independent and private practice settings.',
                  'My approach to supervision is collaborative, reflective and formulation-led, providing a confidential space in which to develop clinical thinking, enhance therapeutic effectiveness and support professional growth. I encourage thoughtful reflection, clinical curiosity and evidence-informed practice, while recognising the complexities and demands of working in psychologically challenging roles.',
                  'Supervision is tailored to the needs and level of experience of each practitioner. Whether you are developing confidence in your clinical practice, working with complex presentations, integrating different therapeutic approaches, preparing for professional accreditation, or seeking an experienced reflective space to support your ongoing development, supervision is adapted to meet your individual goals.']?.
                  map((para, idx) =>
                  <p key={idx} className="ps-body-text">{para}</p>
                  )}
                </div>

                {/* Pull quote */}
                <blockquote
                  style={{
                    borderLeft: '2px solid #8A9E8C',
                    paddingLeft: '24px',
                    margin: '40px 0 0'
                  }}>
                  
                  <p
                    style={{
                      fontFamily: 'orpheus-pro, Georgia, serif',
                      fontSize: 'clamp(17px, 1.6vw, 22px)',
                      fontWeight: '400',
                      color: '#1A2332',
                      lineHeight: '1.55',
                      fontStyle: 'italic'
                    }}>
                    
                    "A confidential space in which to develop clinical thinking, enhance therapeutic effectiveness and support professional growth."
                  </p>
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        {/* ── Accent strip ── */}
        <section
          style={{
            backgroundColor: '#EFE3E1',
            paddingTop: 'clamp(48px, 5vw, 72px)',
            paddingBottom: 'clamp(48px, 5vw, 72px)'
          }}>
          
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '0 clamp(24px, 4vw, 64px)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '48px',
              justifyContent: 'space-between'
            }}>
            
            {[
            { label: 'NHS, Independent & Private Practice', desc: 'Supervision available across all practice settings.' },
            { label: 'Formulation-Led Approach', desc: 'Grounded in clinical curiosity and evidence-informed thinking.' },
            { label: 'Tailored to Your Level', desc: 'From early-career practitioners to experienced clinicians.' }]?.
            map((item, idx) =>
            <div key={idx} style={{ flex: '1 1 240px', maxWidth: '340px' }}>
                <div style={{ width: '32px', height: '1px', backgroundColor: '#8A9E8C', marginBottom: '16px' }} />
                <p
                style={{
                  fontFamily: 'orpheus-pro, Georgia, serif',
                  fontSize: 'clamp(16px, 1.4vw, 20px)',
                  fontWeight: '400',
                  color: '#1A2332',
                  lineHeight: '1.35',
                  marginBottom: '10px'
                }}>
                
                  {item?.label}
                </p>
                <p
                style={{
                  fontFamily: 'adobe-garamond-pro, Georgia, serif',
                  fontSize: '15px',
                  color: '#6A6A6A',
                  lineHeight: '1.7'
                }}>
                
                  {item?.desc}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── Section 4: Training, Workshops & Bespoke Consultancy ── */}
        <section
          className="w-full"
          style={{
            backgroundColor: '#F7F3F2',
            paddingTop: 'clamp(80px, 10vw, 128px)',
            paddingBottom: 'clamp(80px, 10vw, 128px)'
          }}>
          
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            {/* Full-width header */}
            <div style={{ marginBottom: 'clamp(48px, 6vw, 80px)', maxWidth: '720px' }}>
              <span className="ps-section-label">Section Four</span>
              <div className="ps-divider" />
              <h2
                className="ps-section-heading"
                style={{ fontSize: 'clamp(28px, 3.5vw, 52px)', marginBottom: '28px' }}>
                
                Training, Workshops<br />&amp; Bespoke Consultancy
              </h2>
              <p className="ps-body-text">
                I provide tailored training, workshops and consultancy for health professionals and organisations seeking to deepen their understanding of mental health, psychological formulation and complex clinical presentations.
              </p>
            </div>

            {/* Two-column layout */}
            <div className="flex flex-col lg:flex-row items-start gap-16 lg:gap-24">
              {/* Text */}
              <div style={{ flex: '1 1 55%', display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {[
                'My training is designed to be responsive and highly relevant to the needs of each team or organisation. This may include one-off teaching sessions on specific clinical topics, or the development of bespoke training packages addressing particular areas of need such as trauma, neurodevelopmental presentations, emotional dysregulation, risk, or complex case formulation.',
                'Alongside clinical training, I also offer reflective and formulation-led consultation for teams working with complex presentations, supporting the development of shared clinical thinking, confidence and consistency in practice.',
                'I work with NHS teams, independent healthcare providers and corporate organisations, including leadership teams and senior professionals in high-demand roles, where psychological insight is valuable in supporting wellbeing, communication, leadership and performance. Corporate packages can be designed around organisational need, ranging from focused workshops to longer-term consultancy input.',
                'All training and consultancy is developed collaboratively and tailored to the audience, ensuring that content is clinically robust, practically applicable and directly relevant to the challenges faced in real-world settings.']?.
                map((para, idx) =>
                <p key={idx} className="ps-body-text">{para}</p>
                )}

                <div style={{ marginTop: '16px' }}>
                  <a
                    href="/contact"
                    className="cta-btn-dark"
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '13px',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: '#EFE3E1',
                      padding: '15px 40px',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}>
                    
                    Discuss Your Requirements
                  </a>
                </div>
              </div>

              {/* Image */}
              <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '4/3',
                    overflow: 'hidden',
                    backgroundColor: '#DFD0CC'
                  }}>
                  
                  <Image
                    src="/assets/images/image-1783886139520.png"
                    alt="Elegant posh office conference room for training, workshops and bespoke consultancy"
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover"
                    style={{ objectPosition: 'center' }} />
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Closing CTA ── */}
        <section
          className="w-full"
          style={{
            backgroundColor: '#1A2332',
            paddingTop: 'clamp(72px, 9vw, 112px)',
            paddingBottom: 'clamp(72px, 9vw, 112px)'
          }}>
          
          <div
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              padding: '0 clamp(24px, 4vw, 64px)',
              textAlign: 'center'
            }}>
            
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '11px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#8A9E8C',
                marginBottom: '24px'
              }}>
              
              Get in Touch
            </p>
            <h2
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(28px, 3.5vw, 52px)',
                fontWeight: '400',
                color: '#F5F0EA',
                lineHeight: '1.2',
                marginBottom: '24px'
              }}>
              
              Arrange a Consultation
            </h2>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(16px, 1.3vw, 19px)',
                color: 'rgba(239,227,225,0.65)',
                lineHeight: '1.75',
                marginBottom: '40px'
              }}>
              
              If you would like to discuss any of the services above, please feel free to get in touch. All enquiries are handled with complete confidentiality.
            </p>
            <a
              href="/contact"
              className="cta-btn-dark"
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '13px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#EFE3E1',
                padding: '16px 48px',
                textDecoration: 'none',
                display: 'inline-block',
                border: '1px solid rgba(239,227,225,0.25)'
              }}>
              
              Contact Dr Morgan
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>);

}